import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Calendar as CalendarIcon,
  Plus,
  Download,
  RefreshCw as Sync,
  Edit3,
  Trash2,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { leaveApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';

interface HolidayFormData {
  name: string;
  holiday_date: string;
  description?: string;
  location?: string;
  is_mandatory: boolean;
}

interface SyncForm {
  country: string;
  year: string;
}

// Supported countries for sync (Calendarific + Nager.Date APIs)
// Extended list with 180+ countries including India, Sri Lanka, etc.
const SUPPORTED_COUNTRIES = [
  { code: 'AD', name: 'Andorra' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'AF', name: 'Afghanistan' },
  { code: 'AL', name: 'Albania' },
  { code: 'AM', name: 'Armenia' },
  { code: 'AO', name: 'Angola' },
  { code: 'AR', name: 'Argentina' },
  { code: 'AT', name: 'Austria' },
  { code: 'AU', name: 'Australia' },
  { code: 'AZ', name: 'Azerbaijan' },
  { code: 'BA', name: 'Bosnia and Herzegovina' },
  { code: 'BB', name: 'Barbados' },
  { code: 'BD', name: 'Bangladesh' },
  { code: 'BE', name: 'Belgium' },
  { code: 'BF', name: 'Burkina Faso' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'BH', name: 'Bahrain' },
  { code: 'BJ', name: 'Benin' },
  { code: 'BN', name: 'Brunei' },
  { code: 'BO', name: 'Bolivia' },
  { code: 'BR', name: 'Brazil' },
  { code: 'BS', name: 'Bahamas' },
  { code: 'BT', name: 'Bhutan' },
  { code: 'BW', name: 'Botswana' },
  { code: 'BY', name: 'Belarus' },
  { code: 'BZ', name: 'Belize' },
  { code: 'CA', name: 'Canada' },
  { code: 'CD', name: 'DR Congo' },
  { code: 'CF', name: 'Central African Republic' },
  { code: 'CG', name: 'Congo' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'CI', name: 'Ivory Coast' },
  { code: 'CL', name: 'Chile' },
  { code: 'CM', name: 'Cameroon' },
  { code: 'CN', name: 'China' },
  { code: 'CO', name: 'Colombia' },
  { code: 'CR', name: 'Costa Rica' },
  { code: 'CU', name: 'Cuba' },
  { code: 'CY', name: 'Cyprus' },
  { code: 'CZ', name: 'Czech Republic' },
  { code: 'DE', name: 'Germany' },
  { code: 'DJ', name: 'Djibouti' },
  { code: 'DK', name: 'Denmark' },
  { code: 'DO', name: 'Dominican Republic' },
  { code: 'DZ', name: 'Algeria' },
  { code: 'EC', name: 'Ecuador' },
  { code: 'EE', name: 'Estonia' },
  { code: 'EG', name: 'Egypt' },
  { code: 'ER', name: 'Eritrea' },
  { code: 'ES', name: 'Spain' },
  { code: 'ET', name: 'Ethiopia' },
  { code: 'FI', name: 'Finland' },
  { code: 'FJ', name: 'Fiji' },
  { code: 'FR', name: 'France' },
  { code: 'GA', name: 'Gabon' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'GD', name: 'Grenada' },
  { code: 'GE', name: 'Georgia' },
  { code: 'GH', name: 'Ghana' },
  { code: 'GI', name: 'Gibraltar' },
  { code: 'GL', name: 'Greenland' },
  { code: 'GM', name: 'Gambia' },
  { code: 'GN', name: 'Guinea' },
  { code: 'GR', name: 'Greece' },
  { code: 'GT', name: 'Guatemala' },
  { code: 'GY', name: 'Guyana' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'HN', name: 'Honduras' },
  { code: 'HR', name: 'Croatia' },
  { code: 'HT', name: 'Haiti' },
  { code: 'HU', name: 'Hungary' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'IE', name: 'Ireland' },
  { code: 'IL', name: 'Israel' },
  { code: 'IN', name: 'India' },
  { code: 'IQ', name: 'Iraq' },
  { code: 'IR', name: 'Iran' },
  { code: 'IS', name: 'Iceland' },
  { code: 'IT', name: 'Italy' },
  { code: 'JM', name: 'Jamaica' },
  { code: 'JO', name: 'Jordan' },
  { code: 'JP', name: 'Japan' },
  { code: 'KE', name: 'Kenya' },
  { code: 'KG', name: 'Kyrgyzstan' },
  { code: 'KH', name: 'Cambodia' },
  { code: 'KR', name: 'South Korea' },
  { code: 'KW', name: 'Kuwait' },
  { code: 'KZ', name: 'Kazakhstan' },
  { code: 'LA', name: 'Laos' },
  { code: 'LB', name: 'Lebanon' },
  { code: 'LI', name: 'Liechtenstein' },
  { code: 'LK', name: 'Sri Lanka' },
  { code: 'LR', name: 'Liberia' },
  { code: 'LS', name: 'Lesotho' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'LV', name: 'Latvia' },
  { code: 'LY', name: 'Libya' },
  { code: 'MA', name: 'Morocco' },
  { code: 'MC', name: 'Monaco' },
  { code: 'MD', name: 'Moldova' },
  { code: 'ME', name: 'Montenegro' },
  { code: 'MG', name: 'Madagascar' },
  { code: 'MK', name: 'North Macedonia' },
  { code: 'ML', name: 'Mali' },
  { code: 'MM', name: 'Myanmar' },
  { code: 'MN', name: 'Mongolia' },
  { code: 'MO', name: 'Macau' },
  { code: 'MT', name: 'Malta' },
  { code: 'MU', name: 'Mauritius' },
  { code: 'MV', name: 'Maldives' },
  { code: 'MW', name: 'Malawi' },
  { code: 'MX', name: 'Mexico' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'MZ', name: 'Mozambique' },
  { code: 'NA', name: 'Namibia' },
  { code: 'NE', name: 'Niger' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'NI', name: 'Nicaragua' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'NO', name: 'Norway' },
  { code: 'NP', name: 'Nepal' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'OM', name: 'Oman' },
  { code: 'PA', name: 'Panama' },
  { code: 'PE', name: 'Peru' },
  { code: 'PG', name: 'Papua New Guinea' },
  { code: 'PH', name: 'Philippines' },
  { code: 'PK', name: 'Pakistan' },
  { code: 'PL', name: 'Poland' },
  { code: 'PR', name: 'Puerto Rico' },
  { code: 'PS', name: 'Palestine' },
  { code: 'PT', name: 'Portugal' },
  { code: 'PY', name: 'Paraguay' },
  { code: 'QA', name: 'Qatar' },
  { code: 'RO', name: 'Romania' },
  { code: 'RS', name: 'Serbia' },
  { code: 'RU', name: 'Russia' },
  { code: 'RW', name: 'Rwanda' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'SD', name: 'Sudan' },
  { code: 'SE', name: 'Sweden' },
  { code: 'SG', name: 'Singapore' },
  { code: 'SI', name: 'Slovenia' },
  { code: 'SK', name: 'Slovakia' },
  { code: 'SL', name: 'Sierra Leone' },
  { code: 'SM', name: 'San Marino' },
  { code: 'SN', name: 'Senegal' },
  { code: 'SO', name: 'Somalia' },
  { code: 'SR', name: 'Suriname' },
  { code: 'SS', name: 'South Sudan' },
  { code: 'SV', name: 'El Salvador' },
  { code: 'SY', name: 'Syria' },
  { code: 'SZ', name: 'Eswatini' },
  { code: 'TD', name: 'Chad' },
  { code: 'TG', name: 'Togo' },
  { code: 'TH', name: 'Thailand' },
  { code: 'TJ', name: 'Tajikistan' },
  { code: 'TM', name: 'Turkmenistan' },
  { code: 'TN', name: 'Tunisia' },
  { code: 'TR', name: 'Turkey' },
  { code: 'TT', name: 'Trinidad and Tobago' },
  { code: 'TW', name: 'Taiwan' },
  { code: 'TZ', name: 'Tanzania' },
  { code: 'UA', name: 'Ukraine' },
  { code: 'UG', name: 'Uganda' },
  { code: 'US', name: 'United States' },
  { code: 'UY', name: 'Uruguay' },
  { code: 'UZ', name: 'Uzbekistan' },
  { code: 'VA', name: 'Vatican City' },
  { code: 'VE', name: 'Venezuela' },
  { code: 'VN', name: 'Vietnam' },
  { code: 'YE', name: 'Yemen' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'ZM', name: 'Zambia' },
  { code: 'ZW', name: 'Zimbabwe' },
].sort((a, b) => a.name.localeCompare(b.name));

const PublicHolidays: React.FC = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [isSyncOpen, setIsSyncOpen] = useState(false);
  const [isCustomOpen, setIsCustomOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState<any>(null);
  const [syncForm, setSyncForm] = useState<SyncForm>({
    country: '',
    year: new Date().getFullYear().toString()
  });
  const [customForm, setCustomForm] = useState<HolidayFormData>({
    name: '',
    holiday_date: new Date().toISOString().split('T')[0],
    description: '',
    location: '',
    is_mandatory: true,
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());

  // Fetch holidays
  const { data: holidays = [], isLoading, error } = useQuery({
    queryKey: ['publicHolidays', selectedYear],
    queryFn: () => leaveApi.holidays.getAll({ year: selectedYear }),
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: leaveApi.holidays.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['publicHolidays'] });
      setIsCustomOpen(false);
      resetCustomForm();
      toast({ title: 'Holiday added successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => leaveApi.holidays.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['publicHolidays'] });
      setIsEditOpen(false);
      setEditingHoliday(null);
      toast({ title: 'Holiday updated successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: leaveApi.holidays.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['publicHolidays'] });
      toast({ title: 'Holiday deleted successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Helper functions
  const formatDate = (dateStr: string): string => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(new Date(dateStr));
  };

  // Filter holidays based on search
  const filteredHolidays = useMemo(() => {
    return holidays.filter((holiday: any) => {
      const matchesSearch = holiday.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (holiday.description || '').toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    }).sort((a: any, b: any) => new Date(a.holiday_date).getTime() - new Date(b.holiday_date).getTime());
  }, [holidays, searchTerm]);

  // Handle sync public holidays (mock implementation)
  const handleSyncHolidays = async () => {
    if (!syncForm.country || !syncForm.year) {
      toast({ title: 'Error', description: 'Please select country and year', variant: 'destructive' });
      return;
    }

    try {
      // Fetch real holidays from external API via backend
      const response: any = await leaveApi.holidays.sync(syncForm.country, parseInt(syncForm.year));

      // Create holidays in database
      const createdCount = 0;
      for (const holiday of response.holidays) {
        try {
          await createMutation.mutateAsync({
            name: holiday.name,
            holiday_date: holiday.holiday_date,
            description: holiday.description,
            location: holiday.location,
            is_mandatory: holiday.is_mandatory,
          });
        } catch (err) {
          // Skip duplicates or errors for individual holidays
          console.warn('Failed to create holiday:', holiday.name, err);
        }
      }

      setIsSyncOpen(false);
      queryClient.invalidateQueries({ queryKey: ['publicHolidays'] });
      toast({
        title: 'Success',
        description: `Synced ${response.count} holidays for ${SUPPORTED_COUNTRIES.find(c => c.code === syncForm.country)?.name} ${syncForm.year}`
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to sync holidays',
        variant: 'destructive'
      });
    }
  };

  // Handle add custom holiday
  const handleAddCustomHoliday = () => {
    if (!customForm.name.trim()) {
      toast({ title: 'Error', description: 'Holiday name is required', variant: 'destructive' });
      return;
    }

    createMutation.mutate(customForm);
  };

  // Handle edit holiday
  const handleEditHoliday = (holiday: any) => {
    setEditingHoliday(holiday);
    setCustomForm({
      name: holiday.name,
      holiday_date: holiday.holiday_date,
      description: holiday.description || '',
      location: holiday.location || '',
      is_mandatory: holiday.is_mandatory ?? true,
    });
    setIsEditOpen(true);
  };

  // Handle update holiday
  const handleUpdateHoliday = () => {
    if (!editingHoliday || !customForm.name.trim()) {
      toast({ title: 'Error', description: 'Holiday name is required', variant: 'destructive' });
      return;
    }

    updateMutation.mutate({ id: editingHoliday.id, data: customForm });
  };

  // Handle delete holiday
  const handleDeleteHoliday = (holiday: any) => {
    if (confirm(`Are you sure you want to delete "${holiday.name}"?`)) {
      deleteMutation.mutate(holiday.id);
    }
  };

  const resetCustomForm = () => {
    setCustomForm({
      name: '',
      holiday_date: new Date().toISOString().split('T')[0],
      description: '',
      location: '',
      is_mandatory: true,
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading holidays...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded-lg">
        <p className="font-semibold">Error loading holidays</p>
        <p className="text-sm mt-1">{error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Public Holidays</h1>
          <p className="text-gray-600 mt-2">Manage public holidays and company-specific days off</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            className="flex items-center space-x-2"
            onClick={() => {
              // Export holidays to CSV
              const csvRows = [];
              csvRows.push(['Date', 'Holiday Name', 'Description', 'Location', 'Type'].join(','));

              filteredHolidays.forEach((holiday: any) => {
                csvRows.push([
                  holiday.holiday_date,
                  `"${holiday.name}"`,
                  `"${holiday.description || ''}"`,
                  holiday.location || 'All Locations',
                  holiday.is_mandatory ? 'Mandatory' : 'Optional'
                ].join(','));
              });

              const csvString = csvRows.join('\n');
              const blob = new Blob([csvString], { type: 'text/csv' });
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.setAttribute('hidden', '');
              a.setAttribute('href', url);
              a.setAttribute('download', `public-holidays-${selectedYear}.csv`);
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);

              toast({ title: 'Holidays exported successfully' });
            }}
          >
            <Download className="h-4 w-4" />
            <span>Export</span>
          </Button>
          <Button
            onClick={() => setIsSyncOpen(true)}
            variant="outline"
            size="sm"
            className="flex items-center space-x-2"
          >
            <Sync className="h-4 w-4" />
            <span>Sync Holidays</span>
          </Button>
          <Button
            onClick={() => setIsCustomOpen(true)}
            className="flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Add Holiday</span>
          </Button>
        </div>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <CardTitle>Filter & Search</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[300px]">
              <Input
                placeholder="Search holidays by name or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Label className="text-sm font-medium text-gray-700">Year:</Label>
              <Select value={selectedYear.toString()} onValueChange={(value) => setSelectedYear(parseInt(value))}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 5 }, (_, i) => {
                    const year = new Date().getFullYear() - 1 + i;
                    return (
                      <SelectItem key={year} value={year.toString()}>
                        {year}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Holidays Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Holidays ({filteredHolidays.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Holiday Name</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredHolidays.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                    No holidays found matching your criteria
                  </TableCell>
                </TableRow>
              ) : (
                filteredHolidays.map((holiday: any) => (
                  <TableRow key={holiday.id}>
                    <TableCell className="font-medium">
                      {formatDate(holiday.holiday_date)}
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">{holiday.name}</div>
                        {holiday.description && (
                          <div className="text-sm text-gray-500">{holiday.description}</div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {holiday.location || 'All Locations'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={holiday.is_mandatory ? "default" : "secondary"}>
                        {holiday.is_mandatory ? 'Mandatory' : 'Optional'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditHoliday(holiday)}
                        >
                          <Edit3 className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteHoliday(holiday)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Sync Public Holidays Modal */}
      <Dialog open={isSyncOpen} onOpenChange={setIsSyncOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Sync className="h-5 w-5 text-blue-600" />
              <span>Sync Public Holidays</span>
            </DialogTitle>
            <DialogDescription>
              Import official public holidays from external API for a specific country and year
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="sync-country">Country</Label>
              <Select
                value={syncForm.country}
                onValueChange={(value) => setSyncForm(prev => ({ ...prev, country: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a country" />
                </SelectTrigger>
                <SelectContent>
                  {SUPPORTED_COUNTRIES.map(country => (
                    <SelectItem key={country.code} value={country.code}>
                      {country.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="sync-year">Year</Label>
              <Select
                value={syncForm.year}
                onValueChange={(value) => setSyncForm(prev => ({ ...prev, year: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 5 }, (_, i) => {
                    const year = new Date().getFullYear() + i;
                    return (
                      <SelectItem key={year} value={year.toString()}>
                        {year}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="flex items-center space-x-2">
                <AlertCircle className="h-4 w-4 text-blue-600" />
                <span className="text-sm text-blue-800">
                  This will add public holidays for the selected country and year.
                </span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsSyncOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSyncHolidays}
              disabled={!syncForm.country || !syncForm.year}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Sync className="h-4 w-4 mr-2" />
              Sync Holidays
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Custom Holiday Modal */}
      <Dialog open={isCustomOpen} onOpenChange={setIsCustomOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Plus className="h-5 w-5" />
              <span>Add Holiday</span>
            </DialogTitle>
            <DialogDescription>
              Create a company-specific holiday or day off
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="custom-name">Holiday Name *</Label>
              <Input
                id="custom-name"
                placeholder="e.g., Company Picnic Day"
                value={customForm.name}
                onChange={(e) => setCustomForm(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="custom-date">Date *</Label>
              <Input
                id="custom-date"
                type="date"
                value={customForm.holiday_date}
                onChange={(e) => setCustomForm(prev => ({ ...prev, holiday_date: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="custom-location">Location (Optional)</Label>
              <Input
                id="custom-location"
                placeholder="e.g., US, UK, or leave empty for all"
                value={customForm.location}
                onChange={(e) => setCustomForm(prev => ({ ...prev, location: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="custom-description">Description (Optional)</Label>
              <Textarea
                id="custom-description"
                placeholder="Brief description of the holiday..."
                value={customForm.description}
                onChange={(e) => setCustomForm(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCustomOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddCustomHoliday}
              disabled={!customForm.name.trim() || createMutation.isPending}
            >
              {createMutation.isPending ? 'Adding...' : 'Add Holiday'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Holiday Modal */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Edit3 className="h-5 w-5 text-blue-600" />
              <span>Edit Holiday</span>
            </DialogTitle>
            <DialogDescription>
              Update holiday information
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Holiday Name *</Label>
              <Input
                id="edit-name"
                value={customForm.name}
                onChange={(e) => setCustomForm(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-date">Date *</Label>
              <Input
                id="edit-date"
                type="date"
                value={customForm.holiday_date}
                onChange={(e) => setCustomForm(prev => ({ ...prev, holiday_date: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-location">Location (Optional)</Label>
              <Input
                id="edit-location"
                placeholder="e.g., US, UK, or leave empty for all"
                value={customForm.location}
                onChange={(e) => setCustomForm(prev => ({ ...prev, location: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-description">Description (Optional)</Label>
              <Textarea
                id="edit-description"
                value={customForm.description}
                onChange={(e) => setCustomForm(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsEditOpen(false);
              setEditingHoliday(null);
            }}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdateHoliday}
              disabled={!customForm.name.trim() || updateMutation.isPending}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {updateMutation.isPending ? 'Updating...' : 'Update Holiday'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PublicHolidays;